import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import ApiClient from "../../utils/ApiClient";
import SfpRackModal from "./SfpRackModal";

// Separate Tooltip Component
function Tooltip({ children, targetRef }) {
    const [position, setPosition] = useState({ top: 0, left: 0 });

    React.useEffect(() => {
        if (targetRef.current) {
            const rect = targetRef.current.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

            setPosition({
                top: rect.top + scrollTop - 5,
                left: rect.left + scrollLeft + (rect.width / 2)
            });
        }
    }, [targetRef.current]);

    return createPortal(
        <div
            className="absolute pointer-events-none"
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                transform: 'translate(-50%, -100%)',
                zIndex: 50
            }}
        >
            <div className="bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg">
                <p className="text-sm break-words whitespace-normal max-w-sm">
                    {children}
                </p>
                <div className="absolute h-2 w-2 bg-gray-900 transform rotate-45 translate-x-1/2 translate-y-1/2"
                     style={{ bottom: '2px', left: '45%' }} />
            </div>
        </div>,
        document.body
    );
}

// Separate Row Component
function TableRow({
                      item,
                      index,
                      columnWidths,
                      handleDamagedInventoryChange,
                      handleLineItemNotesChange,
                      damagedInventory,
                      lineItemNotes,
                      updating,
                      sfp,
                      onRack
                  }) {
    const cellRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const isComplete = item.packinglist_qty === item.packing_list_received;
    const itemId = item.calibrate_supplier_po_id;

    return (
        <tr className={`${isComplete ? 'bg-green-100' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} 
                      transition-colors duration-200`}
        >
            <td style={{ width: columnWidths.poNumber, maxWidth: columnWidths.poNumber }}
                className="px-2 py-4 text-sm text-gray-500 overflow-hidden">
                <div className="truncate">{item.poNumber}</div>
            </td>
            <td style={{ width: columnWidths.asin, maxWidth: columnWidths.asin }}
                className="px-2 py-4 text-sm text-gray-500 overflow-hidden">
                <div className="truncate">{item.Asin}</div>
            </td>
            <td style={{ width: columnWidths.supplierSku, maxWidth: columnWidths.supplierSku }}
                className="px-2 py-4  text-xl font-bold  text-black overflow-hidden">
                <div className="truncate">{item.supplier_sku}</div>
            </td>
            <td
                ref={cellRef}
                style={{ width: columnWidths.productName, maxWidth: columnWidths.productName }}
                className="px-2 py-4 text-sm text-gray-500 relative"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
            >
                <div className="truncate overflow-hidden" style={{maxWidth: '100%'}}>
                    {item.product_name}
                </div>
                {sfp && sfp.sfpTotal > 0 && (
                    <div className="mt-1 flex items-center gap-2 text-xs">
                        <span className="rounded-full bg-teal-100 px-2 py-0.5 font-medium text-teal-800">SFP {sfp.sfpTotal}</span>
                        {sfp.rackable > 0 ? (
                            <button type="button" onClick={() => onRack(sfp)}
                                    className="rounded-md bg-teal-600 px-2 py-0.5 font-semibold text-white hover:bg-teal-500">
                                Rack SFP ({sfp.rackable})
                            </button>
                        ) : sfp.racked >= sfp.sfpTotal ? (
                            <span className="text-green-700">✓ racked {sfp.racked} of {sfp.sfpTotal}</span>
                        ) : sfp.racked > 0 ? (
                            <span className="text-gray-600">racked {sfp.racked} of {sfp.sfpTotal}</span>
                        ) : null}
                    </div>
                )}
                {showTooltip && (
                    <Tooltip targetRef={cellRef}>
                        {item.product_name}
                    </Tooltip>
                )}
            </td>
            <td style={{ width: columnWidths.packingListQty, maxWidth: columnWidths.packingListQty }}
                className="px-2 py-4 text-xl text-center font-bold text-black overflow-hidden">
                <div className="truncate">{item.packinglist_qty}</div>
            </td>
            <td style={{ width: columnWidths.packingListReceived, maxWidth: columnWidths.packingListReceived }}
                className="px-2 py-4 text-xl text-center font-bold text-black overflow-hidden">
                <div className="truncate">{item.packing_list_received}</div>
            </td>
            <td style={{ width: columnWidths.damagedInventory }} className="px-2 py-4 text-sm text-gray-500 relative">
                <input
                    type="number"
                    min="0"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="0"
                    value={damagedInventory[itemId] ?? item.qty_damaged ?? ''}
                    onChange={(e) => handleDamagedInventoryChange(item, e.target.value)}
                    disabled={updating === itemId}
                />
                {updating === itemId && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500"></div>
                    </div>
                )}
            </td>
            <td className="px-2 py-4 text-sm text-gray-500 relative">
                <input
                    type="text"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Add notes"
                    value={lineItemNotes[itemId] ?? item.item_notes ?? ''}
                    onChange={(e) => handleLineItemNotesChange(item, e.target.value)}
                    disabled={updating === itemId}
                />
                {updating === itemId && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500"></div>
                    </div>
                )}
            </td>
        </tr>
    );
}

// Main Table Component
export default function RAPackingListTable({ packingListData, sfpByAsin = {}, onRacked }) {
    const [columnWidths, setColumnWidths] = useState({
        poNumber: 50,
        asin: 125,
        supplierSku: 125,
        productName: 125,
        packingListQty: 50,
        packingListReceived: 50,
        damagedInventory: 50,
    });

    const [lineItemNotes, setLineItemNotes] = useState({});
    const [damagedInventory, setDamagedInventory] = useState({});
    const [updating, setUpdating] = useState(null);
    const [rackLine, setRackLine] = useState(null);

    const resizingColumn = useRef(null);
    const debounceTimers = useRef({});

    const handleMouseDown = (col) => (event) => {
        event.preventDefault();
        resizingColumn.current = { col, startX: event.clientX, startWidth: columnWidths[col] };
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
    };

    const handleMouseMove = (event) => {
        if (!resizingColumn.current) return;

        const { col, startX, startWidth } = resizingColumn.current;
        const newWidth = Math.max(50, startWidth + (event.clientX - startX));

        setColumnWidths((prevWidths) => ({ ...prevWidths, [col]: newWidth }));
    };

    const handleMouseUp = () => {
        resizingColumn.current = null;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
    };

    const handleDamagedInventoryChange = async (item, value) => {
        const itemId = item.calibrate_supplier_po_id;
        const timerKey = `damaged_${itemId}`;

        setDamagedInventory(prev => ({
            ...prev,
            [itemId]: value
        }));

        if (debounceTimers.current[timerKey]) {
            clearTimeout(debounceTimers.current[timerKey]);
        }

        debounceTimers.current[timerKey] = setTimeout(async () => {
            try {
                setUpdating(itemId);
                const currentNotes = lineItemNotes[itemId] ?? item.item_notes ?? '';
                await ApiClient.post("/api/receiveinventory/packinglist/update", {
                    calibrate_supplier_po_id: itemId,
                    qty_damaged: value ? parseInt(value, 10) : null,
                    item_notes: currentNotes
                });
            } catch (error) {
                console.error('Failed to update damaged inventory:', error);
            } finally {
                setUpdating(null);
            }
        }, 500);
    };

    const handleLineItemNotesChange = async (item, value) => {
        const itemId = item.calibrate_supplier_po_id;
        const timerKey = `notes_${itemId}`;

        setLineItemNotes(prev => ({
            ...prev,
            [itemId]: value
        }));

        if (debounceTimers.current[timerKey]) {
            clearTimeout(debounceTimers.current[timerKey]);
        }

        debounceTimers.current[timerKey] = setTimeout(async () => {
            try {
                setUpdating(itemId);
                const currentDamaged = damagedInventory[itemId] ?? item.qty_damaged ?? null;
                await ApiClient.post("/api/receiveinventory/packinglist/update", {
                    calibrate_supplier_po_id: itemId,
                    qty_damaged: currentDamaged ? parseInt(currentDamaged, 10) : null,
                    item_notes: value
                });
            } catch (error) {
                console.error('Failed to update notes:', error);
            } finally {
                setUpdating(null);
            }
        }, 500);
    };

    React.useEffect(() => {
        return () => {
            Object.values(debounceTimers.current).forEach(timer => {
                if (timer) clearTimeout(timer);
            });
        };
    }, []);

    // Sort packing list data with completed rows first
    const sortedPackingListData = [...packingListData].sort((a, b) => {
        const isCompleteA = a.packinglist_qty === a.packing_list_received;
        const isCompleteB = b.packinglist_qty === b.packing_list_received;

        // Completed rows first, then sort the rest as before
        if (isCompleteA && !isCompleteB) return -1;
        if (!isCompleteA && isCompleteB) return 1;

        return 0; // Maintain original order for rows with the same completion status
    });

    return (
        <div className="px-4 sm:px-6 lg:px-8">
            <div className="mt-0 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block w-full py-2 align-middle sm:px-6 lg:px-8">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead>
                            <tr>
                                <th style={{ width: columnWidths.poNumber }} className="px-2 py-3.5 text-center text-sm font-semibold text-gray-900 relative">
                                    PO Number
                                    <div
                                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 hover:bg-gray-600"
                                        onMouseDown={handleMouseDown('poNumber')}
                                    />
                                </th>
                                <th style={{ width: columnWidths.asin }} className="px-2 py-3.5 text-center text-sm font-semibold text-gray-900 relative">
                                    ASIN
                                    <div
                                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 hover:bg-gray-600"
                                        onMouseDown={handleMouseDown('asin')}
                                    />
                                </th>
                                <th style={{ width: columnWidths.supplierSku }} className="px-2 py-3.5 text-center text-sm font-semibold text-gray-900 relative">
                                    Supplier SKU
                                    <div
                                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 hover:bg-gray-600"
                                        onMouseDown={handleMouseDown('supplierSku')}
                                    />
                                </th>
                                <th style={{ width: columnWidths.productName }} className="px-2 py-3.5 text-center text-sm font-semibold text-gray-900 relative">
                                    Product Name
                                    <div
                                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 hover:bg-gray-600"
                                        onMouseDown={handleMouseDown('productName')}
                                    />
                                </th>
                                <th style={{ width: columnWidths.packingListQty }} className="px-2 py-3.5 text-center text-sm font-semibold text-gray-900 relative">
                                    Packing List Qty
                                    <div
                                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 hover:bg-gray-600"
                                        onMouseDown={handleMouseDown('packingListQty')}
                                    />
                                </th>
                                <th style={{ width: columnWidths.packingListReceived }} className="px-2 py-3.5 text-center text-sm font-semibold text-gray-900 relative">
                                    Packing List Qty Received
                                    <div
                                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 hover:bg-gray-600"
                                        onMouseDown={handleMouseDown('packingListReceived')}
                                    />
                                </th>
                                <th style={{ width: columnWidths.damagedInventory }} className="px-2 py-3.5 text-center text-sm font-semibold text-gray-900 relative">
                                    Damaged Inventory
                                    <div
                                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 hover:bg-gray-600"
                                        onMouseDown={handleMouseDown('damagedInventory')}
                                    />
                                </th>
                                <th className="px-2 py-3.5 text-center text-sm font-semibold text-gray-900">
                                    Line Item Notes
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white">
                            {sortedPackingListData.map((item, index) => (
                                <TableRow
                                    key={item.calibrate_supplier_po_id}
                                    item={item}
                                    index={index}
                                    columnWidths={columnWidths}
                                    handleDamagedInventoryChange={handleDamagedInventoryChange}
                                    handleLineItemNotesChange={handleLineItemNotesChange}
                                    damagedInventory={damagedInventory}
                                    lineItemNotes={lineItemNotes}
                                    updating={updating}
                                    sfp={sfpByAsin[(item.Asin || '').toUpperCase()]}
                                    onRack={setRackLine}
                                />
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {/* The modal closes itself via onClose; onRacked only refreshes, so a push warning stays visible. */}
            {rackLine && (
                <SfpRackModal line={rackLine} onClose={() => setRackLine(null)}
                              onRacked={(res) => { onRacked && onRacked(res); }} />
            )}
        </div>
    );
}